/**
 * PanelConexion — DC-2 (orden 8f3a2596, mockup ratificado 3/3 por el auditor).
 *
 * Superficie visible de `window.aarpia.probarConexiones()` (el cimiento ya existía;
 * esto solo le da cara). Panel deslizante derecho, abierto desde el menú de usuario:
 * "a qué está conectada la app" — API, sesión, catálogo, bus SSE, MCP y versiones.
 *
 * Tres decisiones ratificadas:
 *  1. Ubicación: menú-usuario → panel deslizante (diagnóstico ocasional, no roba espacio).
 *  2. El token JAMÁS se pinta (S23 en la UI): solo su vigencia ("válida hasta HH:MM").
 *  3. Refresco MANUAL: cero polling en reposo — se comprueba al abrir y al pulsar.
 *
 * Estética del molde: sin emojis (puntos de color CSS), relieve sombra+ring, claro/oscuro.
 */
import { useEffect, useState } from 'react'
import { X, RefreshCcw, PlugZap } from 'lucide-react'
import { create } from 'zustand'
import { useAuthStore } from '@/stores/auth'
import { useCatalogStore } from '@/stores/catalog'
import { probeHealth, probeCanonico, probeMcp, probeSse } from '@/lib/asyncapi-devtools'
import { vigenciaSesion } from '@/lib/jwt'
import { Button } from './ui/button'

// Visibilidad global mínima (mismo patrón que el chat store): lo abre el UserMenu.
export const usePanelConexionStore = create<{ open: boolean; setOpen: (v: boolean) => void }>((set) => ({
  open: false,
  setOpen: (open) => set({ open }),
}))

// Render PARCIAL (fix del auditor): cada clave se rellena cuando SU sonda resuelve;
// cada sonda está acotada por timeout y cierra su recurso — el agregado jamás bloquea.
interface Sonda {
  rest_health?: Awaited<ReturnType<typeof probeHealth>>
  rest_canonico?: Awaited<ReturnType<typeof probeCanonico>>
  mcp_streamable?: Awaited<ReturnType<typeof probeMcp>>
  sse_eventos?: Awaited<ReturnType<typeof probeSse>>
}

function Punto({ ok }: { ok: boolean | undefined }) {
  return (
    <span
      aria-label={ok ? 'conectado' : 'sin conexión'}
      className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${ok ? 'bg-emerald-500' : 'bg-red-500'}`}
    />
  )
}

function Fila({ ok, nombre, detalle, ms }: { ok: boolean | undefined; nombre: string; detalle?: string; ms?: number }) {
  return (
    <div className="flex items-center gap-2.5 py-1.5">
      <Punto ok={ok} />
      <span className="text-sm font-medium w-24 shrink-0">{nombre}</span>
      <span className="text-xs text-muted-foreground truncate flex-1">{detalle || '—'}</span>
      {typeof ms === 'number' && <span className="text-[11px] font-mono text-muted-foreground shrink-0">{ms}ms</span>}
    </div>
  )
}

export function PanelConexion() {
  const open = usePanelConexionStore((s) => s.open)
  const setOpen = usePanelConexionStore((s) => s.setOpen)
  const token = useAuthStore((s) => s.token)
  const user = useAuthStore((s) => s.user)
  const contextOwners = useAuthStore((s) => s.contextOwners)
  const activeCoId = useAuthStore((s) => s.activeCoId)
  const entidades = useCatalogStore((s) => s.entidades)

  const [sonda, setSonda] = useState<Sonda | null>(null)
  const [comprobando, setComprobando] = useState(false)
  const [ts, setTs] = useState<number | null>(null)

  async function comprobar() {
    if (comprobando) return
    setComprobando(true)
    setSonda({})
    const H: Record<string, string> = token ? { Authorization: 'Bearer ' + token } : {}
    // Las 4 sondas en PARALELO, cada una pinta su semáforo al resolver (render parcial).
    // Ninguna rechaza y todas están acotadas → el allSettled vuelve en ≤6s SIEMPRE.
    const pinta = (k: keyof Sonda) => (v: any) => setSonda((s) => ({ ...s, [k]: v }))
    await Promise.allSettled([
      probeHealth(H).then(pinta('rest_health')),
      probeCanonico(token, H).then(pinta('rest_canonico')),
      probeMcp(token, H).then(pinta('mcp_streamable')),
      probeSse(token).then(pinta('sse_eventos')),
    ])
    setTs(Date.now())
    setComprobando(false)
  }

  // Una comprobación al ABRIR (no al montar, no polling — decisión 3).
  useEffect(() => {
    if (open) void comprobar()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, setOpen])

  if (!open) return null

  const co = contextOwners.find((c) => c.id === activeCoId)
  const vigencia = vigenciaSesion(token)
  const vApp = entidades.find((e) => e.sub === 'clientes' && e.token === 'app' && e.estado === 'operativa')?.version
  const vInterfaz = entidades.find((e) => e.sub === 'clientes' && e.token === 'interfaz' && e.estado === 'publicada')?.version
  const haceSeg = ts ? Math.max(0, Math.round((Date.now() - ts) / 1000)) : null

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <div className="fixed right-0 top-0 z-50 flex h-screen w-full sm:w-[400px] flex-col bg-background shadow-xl">
        <div className="flex items-center gap-2 p-4 shadow-sm">
          <PlugZap className="h-4 w-4 text-primary" />
          <div className="font-semibold text-sm">Conexión con el organismo</div>
          <Button variant="ghost" size="icon" className="ml-auto" onClick={() => setOpen(false)} title="Cerrar (Esc)" aria-label="Cerrar el panel de conexión">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div className="rounded-xl bg-card p-3.5 shadow-md ring-1 ring-black/5 dark:ring-white/10">
            <Fila ok={sonda?.rest_health?.ok} nombre="API" detalle={sonda?.rest_health?.ok ? `HTTP ${sonda.rest_health.status}` : sonda?.rest_health?.error} ms={sonda?.rest_health?.ms} />
            <Fila
              ok={!!token}
              nombre="Sesión"
              detalle={user ? `${user.nombre?.trim() || user.email} · ${co?.nombre || '—'}${vigencia ? ` · ${vigencia}` : ''}` : 'sin sesión'}
            />
            <Fila
              ok={sonda?.rest_canonico?.ok}
              nombre="Catálogo"
              detalle={sonda?.rest_canonico?.ok ? `${sonda.rest_canonico.entidades} entidades · ${sonda.rest_canonico.subdominios} subdominios` : sonda?.rest_canonico?.error}
              ms={sonda?.rest_canonico?.ms}
            />
            <Fila ok={sonda?.sse_eventos?.ok} nombre="Bus (SSE)" detalle={sonda?.sse_eventos?.estado ?? sonda?.sse_eventos?.error} ms={sonda?.sse_eventos?.connect_ms ?? undefined} />
            <Fila ok={sonda?.mcp_streamable?.ok} nombre="MCP" detalle={sonda?.mcp_streamable?.ok ? `${sonda.mcp_streamable.transporte} · agent-only` : sonda?.mcp_streamable?.error} ms={sonda?.mcp_streamable?.ms} />
          </div>

          <div className="rounded-xl bg-card p-3.5 shadow-md ring-1 ring-black/5 dark:ring-white/10 space-y-1">
            <div className="text-xs font-medium text-muted-foreground">Versiones</div>
            <div className="text-xs font-mono">
              app.operativa {vApp ? `v${vApp}` : '—'} · interfaz.publicada {vInterfaz ? `v${vInterfaz}` : '—'} · API v1
            </div>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-[11px] text-muted-foreground">
              {comprobando ? 'Comprobando…' : haceSeg === null ? 'Sin comprobar todavía' : haceSeg < 5 ? 'Comprobado ahora mismo' : `Última comprobación: hace ${haceSeg}s`}
            </span>
            <Button variant="outline" size="sm" onClick={comprobar} disabled={comprobando} className="shadow-sm">
              <RefreshCcw className={`h-3.5 w-3.5 ${comprobando ? 'animate-spin' : ''}`} />
              Recomprobar
            </Button>
          </div>

          <div className="text-[10px] text-muted-foreground">
            La comprobación es manual: la app no sondea en reposo. El token de sesión nunca se muestra — solo su vigencia.
          </div>
        </div>
      </div>
    </>
  )
}
